// Css =>
import "../../BaseStructure.css";
import Styled from "./showProduct.module.css";

// icons =>
import SaveIcon from "../../Assets/Icons/big-save.png";
import NotSaveIcon from "../../Assets/Icons/small-save.png";

// Service =>
import { ProductDetailReceivre } from "./showProductService";

// react =>
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Types =>
import { ProductBoxType } from "./showProductType";

// API =>
import backEnd from "../../services/Api";

// utils =>
import { slugify } from "../../utils/RemoveUrlSpace";
import { formatPrice } from "../../utils/ChangePriceFormat";
import { LocalCSRFReceiver } from "../../utils/GetLocalCSRF";
import { truncateText } from "../../utils/TruncateProductName";

// context =>
import { useMessage } from "../../Contexts/MessageBoxContext";

// hooks =>
import { useUserStore } from "../../hooks/useUserStore";

function ProductDisplayer({ productId, productThumbnail, productName, productPrice, productColorList }: ProductBoxType) {

    const navigate = useNavigate();
    const { showMessage } = useMessage();
    const [isInWishlist, setIsInWishlist] = useState<boolean>(false);

    useEffect(() => {
        checkWishlist();
    }, [productId]);

    const checkWishlist = async () => {
        if (useUserStore.getState().user?.id) {
            try {
                const response = await backEnd.get(`/products/wishlist/check/`, {
                    params: { product_id: productId }
                });
    
                setIsInWishlist(response.data.in_wishlist);
            } catch (error: any) {
                showMessage("error", "خطا در چک کردن wishlist:");
            }
        }
    };

    const handleAddToWishlist = async (e: React.MouseEvent) => {
        e.stopPropagation();

        try {
            const csrf = await LocalCSRFReceiver();

            const response = await backEnd.post('products/wishlist/add/',
                { product_id: productId },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrf,
                    }
                }
            );

            if (response.status === 200 || response.status === 201) {
                setIsInWishlist(true);
                showMessage("success", "محصول به علاقه‌مندی‌ها اضافه شد ✓");
            }
        } catch (error: any) {
            let errorMessage = "خطای غیر منتظره ای رخ داد!";

            if (error.response) {
                switch (error.response.status) {
                    case 400:
                        errorMessage = "اطلاعات ارسالی نامعتبر است";
                        break;
                    case 401:
                    case 403:
                        useUserStore.getState().setUser(null);
                        localStorage.removeItem("user-storage");

                        errorMessage = "لطفا ابتدا وارد حساب کاربری خود شوید!";
                        navigate('/log-in');
                        break;
                    case 404:
                        errorMessage = "محصول مورد نظر یافت نشد";
                        break;
                    case 409:
                        errorMessage = "این محصول قبلاً به علاقه‌مندی‌ها اضافه شده است";
                        setIsInWishlist(true);
                        break;
                    case 500:
                        errorMessage = "خطای سرور. لطفا بعداً تلاش کنید";
                        break;
                    default:
                        errorMessage = "خطایی رخ داد. لطفا دوباره تلاش کنید";
                }
            } else if (error.request) {
                errorMessage = "خطا در برقراری ارتباط با سرور. اتصال اینترنت خود را بررسی کنید";
            } else {
                errorMessage = "خطای غیرمنتظره. لطفا دوباره تلاش کنید";
            }

            showMessage("error", errorMessage);
        }
    };

    const handleRemoveFromWishlist = async (e: React.MouseEvent) => {
        e.stopPropagation();

        try {
            const csrf = await LocalCSRFReceiver();

            const response = await backEnd.post('products/wishlist/remove/',
                { product_id: productId },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRFToken": csrf,
                    }
                }
            );

            if (response.status === 200 || response.status === 204) {
                setIsInWishlist(false);
                document.location.reload();
                showMessage("success", "محصول از علاقه‌مندی‌ها حذف شد");
            }
        } catch (error: any) {
            let errorMessage = "خطای غیر منتظره ای رخ داد!";

            if (error.response) {
                switch (error.response.status) {
                    case 400:
                        errorMessage = "اطلاعات ارسالی نامعتبر است";
                        break;
                    case 401:
                        useUserStore.getState().setUser(null);
                        localStorage.removeItem("user-storage");

                        errorMessage = "لطفا ابتدا وارد حساب کاربری خود شوید";
                        navigate('/login');
                        break;
                    case 404:
                        errorMessage = "محصول در لیست علاقه‌مندی‌ها یافت نشد";
                        setIsInWishlist(false);
                        break;
                    case 500:
                        errorMessage = "خطای سرور. لطفا بعداً تلاش کنید";
                        break;
                    default:
                        errorMessage = "خطایی رخ داد. لطفا دوباره تلاش کنید";
                }
            } else if (error.request) {
                errorMessage = "خطا در برقراری ارتباط با سرور. اتصال اینترنت خود را بررسی کنید";
            } else {
                errorMessage = "خطای غیرمنتظره. لطفا دوباره تلاش کنید";
            }

            showMessage("error", errorMessage);
        }
    };

    return (
        <div className={`col-2 ${ Styled.ProductBox }`} onClick={async () => {
            const result = await ProductDetailReceivre(productId);

            if (!result.success || !result.data) {
                showMessage("error", result.message);
                return;
            }

            navigate(`/product-info/${ slugify(productName) }/${ productId }`);
        }} data-aos="fade-up">
            <div className={`col-12 ${ Styled.ProductImageFrame }`}>
                <img className={ Styled.ProductImage } src={ productThumbnail } alt="product-thumbnail" />
            </div>

            <h1>{ truncateText(productName, 16) }</h1>

            <h3>قیمت: { truncateText(formatPrice(productPrice), 20) }</h3>

            <div className="row">
                <div className={`col-1 ${ Styled.SaveIconsFrame } `}>
                    {
                        isInWishlist ? (
                            <img src={ SaveIcon } alt="Save" onClick={ handleRemoveFromWishlist } />
                        ) : (
                            <img src={ NotSaveIcon } alt="Not save" onClick={ handleAddToWishlist } />
                        )
                    }
                </div>

                <div className={`col-10 ${ Styled.ColorContainer } `}>
                    <p>موجود در { productColorList.length } رنگ متفاوت</p>
                </div>
            </div>

            {/* <div className={`row ${ Styled.ColorContainer } `}>
                {
                    productColorList.map((color) => (
                        <div
                            key={color.color_code}
                            className={`col-4 ${Styled.ColorBox}`}
                            style={{ backgroundColor: color.color_code }}
                        ></div>
                    ))
                }
            </div> */}

        </div>
    );
}

export default ProductDisplayer;
